import React, { useEffect, useRef, useState } from 'react';
import styles from '../styles/Header.module.scss';
import CustomImage from './CustomImage';
import Link from 'next/link';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { getColor } from '../utils/helpers';
import { BOOK_COUNSELLING_URL, PRIMARY_SOURCE_B2C_LOGIN } from '../utils/b2c/constants';
import { persistSourceContext } from '../utils/b2c/sourceTracking';
import { useRouter } from 'next/router';
import Button from 'react-bootstrap/Button';
import { HIDE_STUDENT_LOGIN } from '../utils/constants';

interface PageLink {
  id: number;
  link: string;
}

interface BookOnlineCounsellingConfig {
  hideOnPages?: PageLink[];
  alwaysShowOnPages?: PageLink[];
}

const normalizePath = (path: string) => {
  const withoutQueryOrHash = path.split(/[?#]/)[0].replace(/\/+$/, '');
  let decodedPath = withoutQueryOrHash;
  try {
    decodedPath = decodeURI(withoutQueryOrHash);
  } catch {
    // malformed escape sequence: fall back to the raw path
  }
  return decodedPath.toLowerCase() || '/';
};

const getLinkPath = (link: string) => {
  try {
    return new URL(link).pathname;
  } catch {
    return link.startsWith('/') ? link : `/${link}`;
  }
};

const matchesAnyPage = (currentPath: string, pages: PageLink[]) =>
  Array.isArray(pages) &&
  pages.some((page) => {
    if (typeof page?.link !== 'string' || !page.link) return false;
    const linkPath = getLinkPath(page.link);

    if (linkPath.endsWith('/*')) {
      const basePath = normalizePath(linkPath.slice(0, -2));
      const prefix = basePath === '/' ? '/' : `${basePath}/`;
      return currentPath.startsWith(prefix);
    }
    return currentPath === normalizePath(linkPath);
  });

// alwaysShowOnPages overrides hideOnPages.
const isBookOnlineCounsellingVisible = (currentPath: string, config?: BookOnlineCounsellingConfig) => {
  const path = normalizePath(currentPath);

  if (matchesAnyPage(path, config?.alwaysShowOnPages ?? [])) return true;
  return !matchesAnyPage(path, config?.hideOnPages ?? []);
};

const Header: React.FC = (props: any) => {
  const router = useRouter();  

  const isBocBtnVisible = isBookOnlineCounsellingVisible(router?.asPath || '', props?.bookOnlineCounselling);

  // Define the function for redirection
  const handleRedirect = () => {
    window.open(BOOK_COUNSELLING_URL, '_blank');
  };

  // Capture the page the user was on when they clicked Student Login,
  // so Frappe can attribute the lead via source_url + primary_source='B2C Login'.
  const handleLoginClick = () => {
    if (typeof window !== 'undefined') {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_LOGIN,
      });
    }
  };

  const [menuNumber, setMenuNumber] = useState<number>(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const clickRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(0);

  const kcBlackLogoCondition =
    router?.pathname === '/student-services/steps-to-study-abroad' ||
    router?.pathname === '/partners/institutions-universities' ||
    router?.pathname === '/entrepreneur/franchise-proposal';

  const handleClickOutside = (event: any) => {
    if (clickRef.current && !clickRef.current.contains(event.target)) {
      deafultNavTheme();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    setViewportWidth(window.innerWidth);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const deafultNavTheme = () => {
    const customStyle = document.documentElement.style;
    const filteredNavColor = getColor(location?.pathname)[0];
    customStyle.setProperty('--navbar-color', filteredNavColor.navColor);
    customStyle.setProperty(
      '--navbar-menu-color',
      filteredNavColor.navMenuColor
    );

    setMenuNumber(0);
    setIsMobileMenuOpen(false);
  };

  const changeNavTheme = (clickedMenu: number) => {
    const customStyle = document.documentElement.style;
    setMenuNumber(clickedMenu);
    customStyle.setProperty('--navbar-color', '#ffffff');
    customStyle.setProperty('--navbar-menu-color', '#0E1B2C');
    customStyle.setProperty('--book-online-link-color', '#FF7361');
    setIsMobileMenuOpen(true);
  };

  const clickMenu = (clickedMenu: number) => {
    if (menuNumber && menuNumber === clickedMenu) {
      deafultNavTheme();
    } else {
      changeNavTheme(clickedMenu);
    }
  };
  const activeClass = (activeNumber: number) =>
    menuNumber === activeNumber ? styles.activeDiv : '';

  const backMenu = (menuName: string) => {
    return (
      <div
        className={`${styles.linksurl} ${styles.mobileMenu}`}
        onClick={() => setMenuNumber(0)}
      >
        <CustomImage
          src='/images/header-menu/left_arrow.svg'
          alt='Left Arrow'
          height='16'
          width='19'
        />
        <div className={styles.flag}>{menuName}</div>
      </div>
    );
  };

  const upArrow = (
    <svg
      className={activeClass(1)}
      xmlns='http://www.w3.org/2000/svg'
      width='12'
      height='12'
      viewBox='0 0 18.606 11.164'
    >
      <path
        data-name='Path 878'
        d='M9.3,11.164,0,2.3,2.412,0,9.3,6.567,16.194,0l2.412,2.3Z'
        transform='translate(18.606 11.164) rotate(180)'
        fill='#ffffff'
      />
    </svg>
  );

  const downArrow = (
    <svg
      className={activeClass(2)}
      xmlns='http://www.w3.org/2000/svg'
      width='12'
      height='20'
      viewBox='0 0 18.606 11.164'
    >
      <path
        data-name='Path 878'
        d='M9.3,11.164,0,2.3,2.412,0,9.3,6.567,16.194,0l2.412,2.3Z'
        fill='#ffffff'
      />
    </svg>
  );

  const showStudentLoginBtn = !HIDE_STUDENT_LOGIN.includes(router.pathname)

  return (
    <>
      <div id='mainheader' ref={clickRef}>
        <div className='topMain'>
          <div className={styles.main}>
            <header id='header-navbar' className={styles.navbar}>
              <a id='kc-logo'  href={router.asPath === '/thank-you' ? undefined : '/'} style={{ cursor: router.asPath === '/thank-you' ? 'default !important' : 'pointer' }} onClick={() => deafultNavTheme()}>
                {/*
                  Defensive fallback for CMS logo fields to avoid runtime crashes
                  when any logo object is missing.
                */}
                {(() => {
                  const selectedLogo =
                    menuNumber || isMobileMenuOpen
                      ? props?.kcLogoBlue
                      : kcBlackLogoCondition
                        ? props?.kcLogoBlack
                        : props?.kcLogoWhite;
                  const logoSrc = selectedLogo?.url || '/images/franchisee/kc-logo.svg';
                  const logoAlt = selectedLogo?.alternativeText || 'KC Overseas';

                  return (
                <CustomImage
                  className={styles.kcLogo}
                  src={logoSrc}
                  alt={logoAlt}
                  width={ viewportWidth > 900 ? 247 : viewportWidth > 480 ? 190 : 150 }
                  height={ viewportWidth > 900 ? 43 : viewportWidth > 480 ? 33 : 26 }
                />
                  );
                })()}
              </a>
              <div className={styles.megaMenu}>
                <div className={styles.mainMenu}>
                  <div
                    id='stud-dest-menu'
                    onClick={() => clickMenu(1)}
                    className={activeClass(1)}
                  >
                    <span style={{ paddingRight: '7px' }}>
                      Study Destinations
                    </span>
                    {menuNumber === 1 ? upArrow : downArrow}
                  </div>
                  <div
                    id='service-menu'
                    onClick={() => clickMenu(2)}
                    className={activeClass(2)}
                  >
                    <span style={{ paddingRight: '7px' }}>Services</span>
                    {menuNumber === 2 ? upArrow : downArrow}
                  </div>
                  <a href='/events'>
                    <div id='upcom-event-menu'>Upcoming Events</div>
                  </a>
                  <a href='/contact-us'>
                    <div id='contact-us-link'>Contact Us</div>
                  </a>
                  <div
                    id='company-menu'
                    onClick={() => clickMenu(3)}
                    className={activeClass(3)}
                  >
                    <span className={styles.companyspan}>Company</span>
                    {menuNumber === 3 ? upArrow : downArrow}
                  </div>
                  <Link href='https://www.coursefinder.ai'>
                    <a id='cofforPartner' target='_blank'>
                      <div
                        style={{
                          lineHeight: '13px',
                          display: 'block',
                          padding: '5px',
                        }}
                        className={`${menuNumber || isMobileMenuOpen ? styles.cfActive : ''
                          } ${styles.externalLinks} ${styles.btnMargin}`}
                      >
                        <span>coursefinder.ai</span>
                        <br />
                        <span className={styles.forprtners}>
                          ( For Partners )
                        </span>
                      </div>
                    </a>
                  </Link>

                  {/* CFM-1514 */}
                  {showStudentLoginBtn && <a id='loginBtn' href='/check-email?from=login' className={styles.loginBtn} onClick={handleLoginClick}>
                    Student Login
                  </a>}
                  {isBocBtnVisible && <Button id='book-online-conse' onClick={handleRedirect} className={styles.onlineCounselling}>
                    <div
                      className={`${menuNumber || isMobileMenuOpen ? styles.onlineConActive : ''} ${styles.bookOnlineLink}`}
                    >
                      <CustomImage
                        src="/new-images/vedioiconsnew.svg"
                        height={22}
                        width={22}
                        alt='onlineCons.png'
                      />
                      <span>Book Online Counselling</span>
                    </div>
                  </Button>}
                </div>
              </div>
              <div className={styles.mobileMenu}>
                {menuNumber || isMobileMenuOpen ? (
                  <div onClick={() => deafultNavTheme()}>
                    <CustomImage src='/cross_icon.svg' height='25' width='30' alt='cross icon'/>
                  </div>
                ) : (
                  <div onClick={() => changeNavTheme(0)}>
                    <CustomImage
                      src='/new-images/frame-a.svg'
                      alt='Frame A'
                      height='25'
                      width='30'
                    />
                  </div>
                )}
              </div>
            </header>
            {/* mobile menu */}
            <div id='mobile-menu' className={styles.mobileMenu}>
              {isMobileMenuOpen && (
                <div className={styles.subMenu}>
                  <div className='container'>
                    <Row className={styles.rowCenter}>
                      <Col lg={8} md={8} xs={12}>
                        <div
                          id='stud-destimenu'
                          className={styles.linksurl}
                          onClick={() => clickMenu(1)}
                        >
                          <CustomImage
                            src='/images/header-menu/study_dest.svg'
                            alt='Study Destination'
                            height='18'
                            width='17'
                          />
                          <div className={styles.flag}>
                            Study Destinations
                            <span> {menuNumber === 1 ? upArrow : downArrow}</span>
                          </div>
                        </div>
                        <div
                          id='services-menu'
                          className={styles.linksurl}
                          onClick={() => clickMenu(2)}
                        >
                          <CustomImage
                            src='/images/header-menu/services.svg'
                            alt='Services'
                            height='18'
                            width='18'
                          />
                          <div className={styles.flag}>
                            Services
                            <span> {menuNumber === 2 ? upArrow : downArrow}</span>
                          </div>
                        </div>
                        <a href='/events'>
                          <div
                            className={styles.linksurl}
                            onClick={() => deafultNavTheme()}
                          >
                            <CustomImage
                              src='/images/header-menu/upcoming.svg'
                              alt='Upcoming Events'
                              height='18'
                              width='18'
                            />
                            <div id='upcom-events-menu' className={styles.flag}>
                              Upcoming Events
                            </div>
                          </div>
                        </a>
                        <a href='/contact-us'>
                          <div
                            className={styles.linksurl}
                            onClick={() => deafultNavTheme()}
                          >
                            <CustomImage
                              src='/contactus.svg'
                              alt='Contact Us'
                              height='18'
                              width='18'
                            />
                            <div id='contact-us-link' className={styles.flag}>
                              Contact Us
                            </div>
                          </div>
                        </a>
                        <div
                          id='company-menu'
                          className={styles.linksurl}
                          onClick={() => clickMenu(3)}
                        >
                          <CustomImage
                            src='/new-images/aboutusmenuicon.svg'
                            alt='About Us'
                            height='18'
                            width='18'
                          />
                          <div className={styles.flag}>
                            Company
                            <span> {menuNumber === 3 ? upArrow : downArrow}</span>
                          </div>
                        </div>
                        <Link href='https://www.coursefinder.ai'>
                          <a id='coffor-partners' target='_blank' className={styles.mobilGalaxy}>
                            <div
                              className={`${styles.cfActive} ${styles.mobileCf}`}
                            >
                              <div>coursefinder.ai</div>
                              <div style={{ paddingLeft: '10px' }}>
                                ( For Partners )
                              </div>
                            </div>
                          </a>
                        </Link>
                          <Link href='/universities'>
                          <a id='coffor-partners' target='_blank' className={styles.mobilGalaxy}>
                            <div
                              className={`${styles.cfActive} ${styles.mobileCf}`}
                            >
                              <div> Search Universities </div>
                            </div>
                          </a>
                        </Link>
                        {/* CFM-1514 */}
                        {isBocBtnVisible && <Button id='book-onlinecons' onClick={handleRedirect} className={styles.mobileOnlineCounselling}>
                          <div
                            className={`${styles.cfActive} ${styles.mobileCf} ${styles.mobilGalaxy}`}
                          >
                            <CustomImage
                              src='/new-images/vediomobileicon.svg'
                              height={22}
                              width={22}
                              alt='onlineCons.png'
                            />
                            <span>Book Online Counselling</span>
                          </div>
                        </Button>}
                        {showStudentLoginBtn && <a id='mobile-login-btn' href='/check-email?from=login' className={styles.mobilGalaxy} onClick={handleLoginClick}>
                          <div
                            className={`${styles.cfActive} ${styles.mobileCf}`}
                          >
                            <div>Student Login</div>
                          </div>
                        </a>}
                      </Col>
                    </Row>
                  </div>
                </div>
              )}
            </div>
            {/* SUB Menu Study Destination */}
            {menuNumber === 1 && (
              <div className={styles.subMenu}>
                <div>
                  <Row className={styles.rowCenter}>
                    <Col lg={8} md={8} xs={12}>
                      <Row>
                        {backMenu('Study Destinations')}
                        <Col lg={4} md={4} xs={12}>
                          <a
                            onClick={() => router.push('/country/study-in-usa')}
                            href='/country/study-in-usa'
                          >
                            <div className={styles.linksurl}>
                              <CustomImage
                                src='/usaflag.svg'
                                alt='USA'
                                height='35'
                                width='35'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                USA
                              </div>
                            </div>
                          </a>
                          <a href='/country/study-in-canada'>
                            <div className={styles.linksurl}>
                              <CustomImage
                                src='/canadaflag.svg'
                                alt='Canada'
                                height='35'
                                width='35'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                Canada
                              </div>
                            </div>
                          </a>
                          <a href='/country/study-in-uk'>
                            <div className={styles.linksurl}>
                              <CustomImage
                                src='/unitedkingdomflag.svg'
                                alt='United Kingdom'
                                height='35'
                                width='35'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                United Kingdom
                              </div>
                            </div>
                          </a>
                        </Col>
                        <Col lg={4} md={4} xs={12}>
                          <a href='/country/study-in-ireland'>
                            <div className={styles.linksurl}>
                              <CustomImage
                                src='/new-images/ireland-flag.svg'
                                alt='Ireland'
                                height='35'
                                width='35'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                Ireland
                              </div>
                            </div>
                          </a>
                          <a href='/country/study-in-australia'>
                            <div className={styles.linksurl}>
                              <CustomImage
                                src='/australiaflag.svg'
                                alt='Australia'
                                height='35'
                                width='35'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                Australia
                              </div>
                            </div>
                          </a>
                          <a href='/country/study-in-new-zealand'>
                            <div className={styles.linksurl}>
                              <CustomImage
                                src='/newzealandflag.svg'
                                alt='New Zealand'
                                height='35'
                                width='35'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                New Zealand
                              </div>
                            </div>
                          </a>
                        </Col>
                        <Col lg={4} md={4} xs={12}>
                          <a href='/study-destinations/europe'>
                            <div className={styles.linksurl}>
                              <CustomImage
                                src='/europflag.svg'
                                alt='Europe'
                                height='35'
                                width='35'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                Europe
                              </div>
                            </div>
                          </a>
                          <a href='/study-destinations/asia'>
                            <div className={styles.linksurl}>
                              <CustomImage
                                src={`${process.env.NEXT_PUBLIC_ASSET_URL}/Group_1_867b98a2d8.svg`}
                                alt='India'
                                height='38'
                                width='38'
                              />
                              <div
                                className={styles.flag}
                                onClick={() => deafultNavTheme()}
                              >
                                Asia
                              </div>
                            </div>
                          </a>
                          <a target="_blank" href='/universities'> 
                            <div className={styles.searchUniversity}>Search Universities </div>
                          </a>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
            {/* Services */}
            {menuNumber === 2 && (
              <div className={styles.subMenu}>
                <div className='container'>
                  <Row className={styles.rowCenter}>
                    <Col lg={12} md={12} xs={12}>
                      <Row className={styles.studyDesti}>
                        {backMenu('Services')}
                        <Col lg={9} md={9} xs={12}>
                          <a href='/student-services/steps-to-study-abroad'>
                            <div
                              className={styles.linksurl}
                              onClick={() => deafultNavTheme()}
                            >
                              <CustomImage
                                src='/new-images/for-student.svg'
                                alt='Students'
                                height='18'
                                width='18'
                              />
                              <div className={`${styles.flag} ${styles.ipadmenu}`}>For Student </div>
                            </div>
                          </a>
                          <Row>
                            <Col lg={5} md={5} xs={12}>
                              <a href='/student-services/steps-to-study-abroad#1'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                    Counselling
                                  </div>
                                </div>
                              </a>
                              <a href='/test-preparation'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                    Test Preparation
                                  </div>
                                </div>
                              </a>
                              <a href='/universities'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                  Country & University Selection
                                  </div>
                                </div>
                              </a>
                            </Col>
                            <Col lg={4} md={4} xs={12}>
                              <a href='/student-services/steps-to-study-abroad#4'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                    Applications & Admission
                                  </div>
                                </div>
                              </a>
                              <a href='/student-services/study-abroad-scholarships'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                    Scholarships
                                  </div>
                                </div>
                              </a>
                              <a href='/internships'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                    Internship
                                  </div>
                                </div>
                              </a>
                            </Col>
                            <Col lg={3} md={3} xs={12}>
                              <a href='/student-services/education-loan-for-overseas-education'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                    Education Loan
                                  </div>
                                </div>
                              </a>
                              <a href='/student-services/steps-to-study-abroad#8'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                    Visa Processing
                                  </div>
                                </div>
                              </a>
                              <a href='/student-services/allied-services'>
                                <div className={styles.sublinksurl}>
                                  <div
                                    className={styles.flaginner}
                                    onClick={() => deafultNavTheme()}
                                  >
                                   Allied Services
                                  </div>
                                </div>
                              </a>
                            </Col>
                          </Row>
                        </Col>
                        <Col lg={3} md={3} xs={12}>
                          <a href='/partners/institutions-universities'>
                            <div
                              className={styles.linksurl}
                              onClick={() => deafultNavTheme()}
                            >
                              <CustomImage
                                src='/new-images/for-institute.svg'
                                alt='Institutions'
                                height='18'
                                width='18'
                              />
                              <div className={styles.flag}>For Institutes </div>
                            </div>
                          </a>
                          <a href='/partners/study-abroad-association'>
                            <div
                              className={styles.linksurl}
                              onClick={() => deafultNavTheme()}
                            >
                              <CustomImage
                                src='/new-images/for-partners.svg'
                                alt='Partners'
                                height='18'
                                width='17'
                              />
                              <div className={`${styles.flag} ${styles.ipadmenu}`}>For Partners </div>
                            </div>
                          </a>
                          <a href='/entrepreneur/franchise-proposal'>
                            <div
                              className={styles.linksurl}
                              onClick={() => deafultNavTheme()}
                            >
                              <CustomImage
                                src='/new-images/for-franchises.svg'
                                alt='Franchise'
                                height='18'
                                width='18'
                              />
                              <div className={`${styles.flag} ${styles.ipadmenu}`}>For Franchisees </div>
                            </div>
                          </a>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
            {/* Company */}
            {menuNumber === 3 && (
              <div className={styles.subMenu}>
                <div className='container'>
                  <Row className={styles.rowCenter}>
                    <Col lg={12} md={12} xs={12}>
                      <Row className={`{styles.studyDesti}, justify-content-center`}>
                        {backMenu('Company')}
                        <Col lg={3} md={4} xs={12}>
                          <a href='/aboutus'>
                            <div
                              className={styles.linksurl}
                              onClick={() => deafultNavTheme()}
                            >
                              <CustomImage
                                src='/new-images/aboutusmenuicon.svg'
                                alt='About Us'
                                height='18'
                                width='18'
                              />
                              <div className={styles.flag}>About Us</div>
                            </div>
                          </a>
                          <a href='/news'>
                            <div
                              className={styles.linksurl}
                              onClick={() => deafultNavTheme()}
                            >
                              <CustomImage
                                src='/new-images/newpressmenuicon.svg'
                                height='18'
                                width='17'
                                alt='News and Press'
                              />
                              <div className={styles.flag}>News & Press </div>
                            </div>
                          </a>
                        </Col>
                        <Col lg={3} md={4} xs={12} className={styles.mobileCompMenu}>
                          <a href='/blogs'>
                            <div
                              className={styles.linksurl}
                              onClick={() => deafultNavTheme()}
                            >
                              <CustomImage
                                src='/new-images/blogsmenuicon.svg'
                                alt='Blogs'
                                height='18'
                                width='18'
                              />
                              <div className={styles.flag}>Blog</div>
                            </div>
                          </a>

                          <a href='/careers'>
                            <div className={styles.carrerlinkdiv}>
                              <div
                                className={styles.linksurl}
                                onClick={() => deafultNavTheme()}
                              >
                                <CustomImage
                                  src='/new-images/careermenuicon.svg'
                                  alt='Career'
                                  height='18'
                                  width='17'
                                />
                                <div className={styles.flag}>Careers </div>
                              </div>
                              <span className={styles.hiring}>WE’RE HIRING!</span>
                              <span className={styles.ipad}>HIRING!</span>
                            </div>
                          </a>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
